/**
 * Styles for the shadow tree, from the widget design (docs/desings/widget,
 * ADR-0059).
 *
 * Everything customer-specific derives from one accent colour, already
 * checked by Drupal to keep white text readable (>= 4.5:1). Customers never
 * supply CSS: with arbitrary CSS every redesign would break N customers'
 * overrides and we would support N designs instead of one (ADR-0024).
 *
 * `all: initial` on the host is belt and braces: the shadow boundary stops the
 * page's styles reaching in, but the host itself lives in the page.
 */

// Fixed palette of the design: warm neutrals around the accent.
const INK = '#1C1B19';
const MUTED = '#5F5B53';
const LINE = '#E6E2DA';
const FIELD = '#CFC9BE';
const BOT_BUBBLE = '#F1EEE8';
const ALERT_BG = '#FBEFE4';
const ALERT_INK = '#6B3410';
const REC_RED = '#D92D20';
const OUTLINE_LIGHT = '#F4F5F7';
const OUTLINE_DARK = '#A7ADB5';

function outline(px) {
  return [
    `drop-shadow(${px}px 0 0 ${OUTLINE_LIGHT})`, `drop-shadow(-${px}px 0 0 ${OUTLINE_LIGHT})`,
    `drop-shadow(0 ${px}px 0 ${OUTLINE_LIGHT})`, `drop-shadow(0 -${px}px 0 ${OUTLINE_LIGHT})`,
    `drop-shadow(${px}px 0 0 ${OUTLINE_DARK})`, `drop-shadow(-${px}px 0 0 ${OUTLINE_DARK})`,
    `drop-shadow(0 ${px}px 0 ${OUTLINE_DARK})`, `drop-shadow(0 -${px}px 0 ${OUTLINE_DARK})`,
  ].join(' ');
}

export function styles({ accent, position, font }) {
  const side = position === 'bottom-left' ? 'left' : 'right';
  const inward = side === 'right' ? 'row' : 'row-reverse';
  return `
    :host {
      all: initial;
      position: fixed;
      bottom: max(24px, env(safe-area-inset-bottom, 0px));
      ${side}: 24px;
      z-index: 2147483000;
      color-scheme: light;
    }
    /* Typography is set on the widget's own roots, not on :host: the host
       lives in the customer's page, and a page rule like "* { font-family }"
       beats :host and would be inherited by everything inside. */
    .launcher-area, .panel {
      font-family: "${font}", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
      font-size: 15px; line-height: 1.4; color: ${INK};
      -webkit-font-smoothing: antialiased;
    }
    *, *::before, *::after { box-sizing: border-box; }
    [hidden] { display: none !important; }
    button { font: inherit; color: inherit; }
    :focus-visible { outline: 3px solid ${accent}; outline-offset: 2px; }

    .visually-hidden {
      position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px;
      overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; border: 0;
    }
    .icon-button {
      width: 44px; height: 44px; flex-shrink: 0; border: 0; border-radius: 10px;
      background: transparent; display: flex; align-items: center; justify-content: center;
      cursor: pointer;
    }

    /* --- Closed: greeting and mascot ------------------------------------- */
    .launcher-area {
      display: flex; flex-direction: ${inward}; align-items: flex-start; gap: 4px;
    }
    .greeting {
      display: flex; align-items: flex-start; gap: 4px; max-width: 240px; margin-top: 8px;
      padding: 12px 6px 12px 16px; background: #fff;
      border-radius: ${side === 'right' ? '16px 16px 4px 16px' : '16px 16px 16px 4px'};
      box-shadow: 0 8px 28px rgba(28, 27, 25, 0.16);
    }
    .greeting__text {
      display: flex; flex-direction: column; gap: 2px; padding: 2px 0 0; border: 0;
      background: transparent; text-align: start; cursor: pointer;
    }
    .greeting__name { font-size: 14px; font-weight: 700; }
    .greeting__message { font-size: 15px; line-height: 1.4; }
    .greeting__close { color: ${MUTED}; }

    .mascot {
      display: block; width: 128px; height: 140px; flex-shrink: 0; padding: 0; border: 0;
      border-radius: 16px; background: transparent; cursor: pointer; perspective: 700px;
      position: relative;
    }
    /* A person's messages the visitor hasn't seen (ADR-0097). */
    .mascot__badge {
      position: absolute; top: 2px; ${side === 'right' ? 'left' : 'right'}: 2px; min-width: 26px; height: 26px;
      padding: 0 7px; border-radius: 13px; border: 2px solid #fff; background: #b42318; color: #fff;
      font-size: 14px; font-weight: 800; line-height: 22px; text-align: center;
      box-shadow: 0 2px 6px rgba(28, 27, 25, 0.3);
    }
    .mascot__card {
      position: relative; display: block; width: 100%; height: 100%;
      transform-style: preserve-3d; transition: transform 0.8s cubic-bezier(0.45, 0.05, 0.25, 1);
    }
    .mascot--flipped .mascot__card { transform: rotateY(180deg); }
    .mascot__face {
      position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
      backface-visibility: hidden; -webkit-backface-visibility: hidden;
    }
    .mascot__face--back { transform: rotateY(180deg); }
    .mascot__image {
      width: 100%; height: 100%; object-fit: contain;
      filter: ${outline(1.5)} drop-shadow(0 8px 14px rgba(28, 27, 25, 0.25));
    }
    .mascot__disc {
      width: 122px; height: 122px; border-radius: 50%; background: #fff;
      border: 3px solid ${OUTLINE_DARK};
      box-shadow: inset 0 0 0 2px ${OUTLINE_LIGHT}, 0 8px 18px rgba(28, 27, 25, 0.25);
      display: flex; flex-direction: column; align-items: center; justify-content: center;
      gap: 4px; text-align: center;
    }
    .mascot__avatar { width: 40px; height: 40px; border-radius: 50%; object-fit: cover; }
    .mascot__phrase {
      font-size: 17px; font-weight: 800; line-height: 1.1; color: ${accent}; padding: 0 8px;
    }

    @media (max-width: 640px) {
      :host { ${side}: 12px; bottom: max(16px, env(safe-area-inset-bottom, 0px)); }
      .mascot { width: 84px; height: 92px; border-radius: 14px; }
      .mascot__image { filter: ${outline(1)} drop-shadow(0 6px 12px rgba(28, 27, 25, 0.3)); }
      .mascot__disc { width: 80px; height: 80px; gap: 2px; }
      .mascot__avatar { width: 28px; height: 28px; }
      .mascot__phrase { font-size: 12px; }
    }
    @media (prefers-reduced-motion: reduce) {
      .mascot__card { transition: none; }
    }

    /* --- Open: the chat panel ---------------------------------------------- */
    .panel {
      display: flex; flex-direction: column;
      width: min(380px, calc(100vw - 32px));
      height: min(624px, calc(100vh - 48px));
      background: #fff; border-radius: 20px; overflow: hidden;
      box-shadow: 0 12px 40px rgba(28, 27, 25, 0.16);
    }
    .header {
      display: flex; flex-direction: column; gap: 10px; padding: 14px 12px 12px 20px;
      background: ${accent}; color: #fff; flex-shrink: 0;
    }
    .header__top { display: flex; align-items: center; gap: 12px; }
    .header__avatar {
      width: 40px; height: 40px; flex-shrink: 0; border-radius: 50%; background: #fff; object-fit: cover;
    }
    .header__text { flex-grow: 1; display: flex; flex-direction: column; gap: 2px; min-width: 0; }
    .header__name { font-size: 16px; font-weight: 700; }
    .header__subtitle { font-size: 13px; line-height: 1.35; }
    .header__actions { display: flex; flex-wrap: wrap; gap: 6px; }
    .header__actions[hidden] { display: none; }
    .header__action {
      display: inline-flex; align-items: center; gap: 6px; min-height: 32px; padding: 0 12px;
      border: 1px solid rgba(255,255,255,.6); border-radius: 999px; background: transparent;
      color: inherit; font: inherit; font-size: 13px; white-space: nowrap; cursor: pointer;
    }
    .header__action[hidden] { display: none; }
    a.header__action { text-decoration: none; }
    .header__action.header__action--icon { padding: 0; width: 32px; justify-content: center; }
    .header__action--icon[aria-pressed="false"] { opacity: .8; }
    .header__action:hover, .header__action[aria-expanded="true"] { background: rgba(255,255,255,.16); }
    .header__action:focus-visible { outline: 2px solid #fff; outline-offset: 2px; }
    .header__close { color: #fff; border-radius: 12px; }
    .header__close:focus-visible { outline-color: #fff; outline-offset: -4px; }

    .log {
      flex-grow: 1; overflow-y: auto; display: flex; flex-direction: column; gap: 12px;
      padding: 20px;
    }
    .log:focus-visible { outline: 2px solid ${accent}; outline-offset: -2px; }
    .log__day { align-self: center; font-size: 12px; color: ${MUTED}; }
    .bubble {
      margin: 0; max-width: 280px; padding: 12px 14px; font-size: 15px; line-height: 1.45;
      white-space: pre-wrap; overflow-wrap: anywhere;
    }
    .bubble--bot { align-self: flex-start; background: ${BOT_BUBBLE}; border-radius: 4px 16px 16px 16px; }
    .bubble--user { align-self: flex-end; background: ${accent}; color: #fff; border-radius: 16px 4px 16px 16px; }
    .bubble--unsent { background: #fff; color: ${INK}; border: 1px dashed #8C877D; }

    .typing { display: flex; align-items: center; padding: 14px 16px; }
    .typing__dots { display: inline-flex; gap: 5px; }
    .typing__dots span { width: 8px; height: 8px; border-radius: 50%; background: ${MUTED}; }
    .typing__dots span:nth-child(2) { background: #8C877D; }
    .typing__dots span:nth-child(3) { background: #B5B0A6; }

    .unsent { align-self: flex-end; display: flex; flex-direction: column; align-items: flex-end; gap: 4px; }
    .unsent__note { font-size: 12px; color: ${MUTED}; }
    .alert {
      align-self: stretch; display: flex; gap: 10px; align-items: flex-start; padding: 12px 14px;
      background: ${ALERT_BG}; border-radius: 12px; color: ${ALERT_INK};
    }
    .alert > svg { flex-shrink: 0; margin-top: 1px; }
    .alert__body { flex-grow: 1; display: flex; flex-direction: column; gap: 10px; font-size: 15px; line-height: 1.45; }
    .alert__retry {
      align-self: flex-start; min-height: 44px; padding: 0 16px; border: 1.5px solid ${ALERT_INK};
      border-radius: 10px; background: transparent; color: ${ALERT_INK}; font-size: 15px;
      font-weight: 600; cursor: pointer;
    }

    .composer {
      position: relative; display: flex; gap: 8px; align-items: flex-end;
      padding: 12px 12px 8px 16px; border-top: 1px solid ${LINE}; flex-shrink: 0;
    }
    .composer__input {
      flex-grow: 1; min-width: 0; min-height: 44px; max-height: 120px; resize: none;
      border: 1px solid ${FIELD}; border-radius: 12px; padding: 11px 12px;
      font: inherit; font-size: 15px; line-height: 1.4; color: ${INK}; background: #fff;
    }
    .composer__input:focus-visible { outline: 2px solid ${accent}; outline-offset: 1px; }
    .composer__send {
      width: 44px; height: 44px; flex-shrink: 0; border: 0; border-radius: 12px;
      background: ${accent}; color: #fff; display: flex; align-items: center; justify-content: center;
      cursor: pointer;
    }
    .composer__send:disabled { background: ${FIELD}; color: ${INK}; cursor: default; }
    .composer__counter {
      position: absolute; top: -20px; ${side === 'right' ? 'right' : 'left'}: 16px;
      font-size: 12px; color: ${MUTED};
    }
    /* Dictation (ADR-0101). */
    .composer__mic {
      width: 44px; height: 44px; flex-shrink: 0; border: 1px solid ${FIELD}; border-radius: 12px;
      background: #fff; color: ${INK}; display: flex; align-items: center; justify-content: center; cursor: pointer;
    }
    /* While recording or transcribing, the bar takes the field's place. */
    .composer--recording > :is(.composer__input, .composer__mic, .composer__send, .composer__counter),
    .composer--transcribing > :is(.composer__input, .composer__mic, .composer__send, .composer__counter) { display: none; }
    .rec {
      flex-grow: 1; min-width: 0; min-height: 44px; display: flex; align-items: center; gap: 10px;
      border: 1px solid ${FIELD}; border-radius: 12px; padding: 0 0 0 4px; background: #fff; color: ${INK};
      font-size: 14px;
    }
    .rec[hidden] { display: none; }
    .rec:focus { outline: none; }
    .rec__cancel {
      width: 36px; height: 36px; flex-shrink: 0; border: 0; border-radius: 10px; background: transparent;
      color: ${MUTED}; display: flex; align-items: center; justify-content: center; cursor: pointer;
    }
    .rec__cancel:hover { background: ${BOT_BUBBLE}; color: ${INK}; }
    /* Red on white is 4.6:1, and the word "Recording" is next to it: never colour alone. */
    .rec__dot { width: 10px; height: 10px; flex-shrink: 0; border-radius: 50%; background: ${REC_RED}; }
    .rec__label { font-weight: 600; white-space: nowrap; }
    .rec__time { color: ${MUTED}; font-variant-numeric: tabular-nums; white-space: nowrap; }
    .rec__bars { display: flex; align-items: center; gap: 3px; height: 20px; margin-inline-start: auto; }
    .rec__bars span { width: 3px; height: 100%; border-radius: 2px; background: ${INK}; transform: scaleY(.2); transition: transform 90ms linear; }
    .rec__done {
      width: 44px; height: 44px; flex-shrink: 0; margin: -1px -1px -1px 0; border: 0; border-radius: 12px;
      background: ${accent}; color: #fff; display: flex; align-items: center; justify-content: center; cursor: pointer;
    }
    .composer--transcribing .rec { padding-inline-start: 12px; }
    .composer--transcribing :is(.rec__cancel, .rec__dot, .rec__time, .rec__bars, .rec__done) { display: none; }
    .composer--transcribing .rec__label { font-weight: 400; color: ${MUTED}; }
    /* The visitor's vote on one answer (ADR-0102). */
    .votes { align-self: flex-start; display: flex; gap: 8px; margin-top: -4px; }
    .vote {
      width: 32px; height: 32px; border: 1px solid ${LINE}; border-radius: 8px; background: #fff;
      color: ${MUTED}; display: flex; align-items: center; justify-content: center; cursor: pointer;
    }
    .vote:hover { color: ${INK}; border-color: ${FIELD}; }
    .vote[aria-pressed="true"] { background: ${accent}; border-color: ${accent}; color: #fff; }
    .note { align-self: center; max-width: 90%; font-size: 12px; color: ${MUTED}; text-align: center; }
    .card { align-self: stretch; display: flex; flex-direction: column; gap: 8px; padding: 12px; border: 1px solid ${LINE}; border-radius: 12px; background: #fff; }
    .card__text { margin: 0; font-size: 14px; color: ${INK}; }
    .typing__label { font-size: 13px; color: ${MUTED}; margin-right: 6px; }
    .card__hint { margin: 0; font-size: 13px; color: ${MUTED}; }
    .note__button { display: block; margin: 8px auto 0; font-size: 13px; }
    .card__button { padding: 8px 12px; border: 0; border-radius: 10px; background: ${accent}; color: #fff; font: inherit; font-size: 14px; cursor: pointer; }
    .card__button--secondary { background: transparent; color: ${INK}; border: 1px solid ${FIELD}; }
    /* A link drawn as a button: WhatsApp (ADR-0110), a ticket. */
    a.card__button { display: inline-flex; align-items: center; justify-content: center; gap: 6px; text-decoration: none; }
    .whatsapp-link { align-self: flex-start; margin-top: -4px; }
    .card__button:disabled { opacity: .6; cursor: default; }
    .card__button:focus-visible, .handoff-bar__button:focus-visible { outline: 2px solid ${accent}; outline-offset: 2px; }
    /* An action (ADR-0108): choices as buttons, the summary to confirm. */
    .action-options { align-self: flex-start; display: flex; flex-wrap: wrap; gap: 6px; max-width: 90%; }
    .action-option { min-height: 36px; border-radius: 999px; }
    .action-card__title { font-weight: 600; }
    .action-card__list { margin: 0; display: grid; grid-template-columns: auto minmax(0, 1fr); gap: 4px 12px; font-size: 14px; }
    .action-card__list dt { color: ${MUTED}; }
    .action-card__list dd { margin: 0; color: ${INK}; overflow-wrap: anywhere; }
    .action-card__buttons { display: flex; flex-wrap: wrap; gap: 6px; }
    .action-card__buttons .card__button { min-height: 40px; }
    .leave__field { display: flex; flex-direction: column; gap: 4px; font-size: 13px; color: ${MUTED}; }
    .leave__input { font: inherit; font-size: 14px; color: ${INK}; padding: 8px 10px; border: 1px solid ${FIELD}; border-radius: 8px; resize: vertical; }
    .leave__input[aria-invalid="true"] { border-color: ${ALERT_INK}; }
    .leave__error { margin: -4px 0 0; font-size: 13px; color: ${ALERT_INK}; }
    .leave__error[hidden] { display: none; }
    /* "Get the reply by email if I leave" (ADR-0097). */
    .email-offer { display: flex; flex-direction: column; align-items: center; gap: 4px; }
    .email-bar { padding: 4px 16px 0; flex-shrink: 0; font-size: 12px; text-align: center; }
    .email-bar[hidden] { display: none; }
    .note__text { margin: 0; }
    .link-button { min-height: 32px; padding: 4px 8px; border: 0; background: transparent; color: ${INK}; font-size: 13px; text-decoration: underline; text-underline-offset: 2px; cursor: pointer; }
    .email-offer__form { align-self: stretch; display: flex; flex-direction: column; gap: 8px; padding: 12px; border: 1px solid ${LINE}; border-radius: 12px; background: #fff; text-align: start; }
    .person { align-self: flex-start; display: flex; flex-direction: column; gap: 2px; max-width: 85%; }
    .person__name { font-size: 12px; color: ${MUTED}; padding-left: 4px; }
    .bubble--person { max-width: none; }
    .handoff-bar { display: flex; justify-content: center; padding: 6px 16px 0; flex-shrink: 0; }
    .handoff-bar[hidden] { display: none; }
    .composer[hidden] { display: none; }
    .handoff-bar__button { padding: 6px 12px; border: 1px solid ${FIELD}; border-radius: 999px; background: #fff; color: ${INK}; font: inherit; font-size: 13px; cursor: pointer; }
    /* The authorization asked before chatting (ADR-0089). */
    .consent { align-self: stretch; gap: 12px; background: ${BOT_BUBBLE}; border: 0; border-radius: 4px 16px 16px 16px; padding: 14px; }
    .consent:focus { outline: none; }
    .consent:focus-visible { outline: 2px solid ${accent}; outline-offset: 2px; }
    .consent .card__text { overflow-wrap: anywhere; }
    .consent__message { font-size: 15px; line-height: 1.45; color: ${INK}; overflow-wrap: anywhere; }
    .consent__message p { margin: 0 0 8px; }
    .consent__message p:last-child { margin-bottom: 0; }
    .consent__message a { color: ${INK}; text-decoration: underline; }
    .consent .card__button { min-height: 44px; border-radius: 999px; font-weight: 600; }
    .consent-menu { position: relative; display: flex; flex-direction: column; gap: 8px; margin: 0; padding: 12px 16px; border-bottom: 1px solid ${LINE}; background: #fff; font-size: 13px; color: ${INK}; flex-shrink: 0; }
    .consent-menu[hidden] { display: none; }
    .consent-menu__title { margin: 0; font-size: 14px; font-weight: 700; }
    .consent-menu__line { margin: 0; color: ${MUTED}; }
    .consent-menu__id { font-family: ui-monospace, Menlo, monospace; user-select: all; -webkit-user-select: all; color: ${INK}; overflow-wrap: anywhere; }
    .consent-menu .card__button { align-self: flex-start; }
    .disclaimer { padding: 0 16px 10px; font-size: 12px; color: ${MUTED}; text-align: center; flex-shrink: 0; }
    .disclaimer__report { color: ${MUTED}; text-decoration: underline; }
    .branding { margin-top: 2px; }
    .branding__name { color: ${MUTED}; font-weight: 600; }
    a.branding__name { text-decoration: underline; }

    /* Phones: the open panel is the whole screen, clear of the notch and the
       home indicator. 16px in the field keeps iOS from zooming in on focus. */
    @media (max-width: 640px) {
      .panel {
        position: fixed; inset: 0; width: 100%; height: 100%; height: 100dvh;
        border-radius: 0; box-shadow: none;
      }
      .header { padding-top: max(14px, env(safe-area-inset-top, 0px)); padding-left: 16px; }
      /* Three actions in one row down to a 360px phone. */
      .header__actions { gap: 4px; }
      .header__action { gap: 4px; padding: 0 10px; }
      .composer { padding-bottom: max(8px, env(safe-area-inset-bottom, 0px)); }
      .composer__input { font-size: 16px; }
      .log { overscroll-behavior: contain; }
    }

    @media (prefers-reduced-motion: no-preference) {
      .panel { animation: rise 180ms ease-out; }
      .greeting { animation: rise 220ms ease-out; }
      .mascot__badge { animation: rise 220ms ease-out; }
      @keyframes rise { from { opacity: 0; transform: translateY(8px); } }
      .typing__dots span { animation: blink 1.2s infinite ease-in-out both; }
      .typing__dots span:nth-child(2) { animation-delay: 0.15s; }
      .typing__dots span:nth-child(3) { animation-delay: 0.3s; }
      .rec__dot { animation: recording 1.2s infinite ease-in-out; }
      @keyframes recording { 50% { opacity: .35; } }
      @keyframes blink { 0%, 80%, 100% { opacity: 0.35; } 40% { opacity: 1; } }
    }
  `;
}
